import type { ReadConfigOptions } from '@ms-cloudpack/config';
import type { PackageJson } from '@ms-cloudpack/common-types';
import { readJson } from '@ms-cloudpack/json-utilities';
import {
  TaskReporter,
  bold,
  debugLoggingConfig,
  defaultLoggingConfig,
  noLoggingConfig,
  red,
  verboseLoggingConfig,
  yellow,
} from '@ms-cloudpack/task-reporter';
import { startTelemetry } from '@ms-cloudpack/telemetry';
import path from 'path';
import type { CommandAction, CommandExitParams, AppCommandContext } from '../types/CommandAction.js';
import type { ProgramOptions } from '../types/ProgramOptions.js';
import type { SharedOptions } from '../types/SharedOptions.js';
import { AutoDisposableList } from './AutoDisposableList.js';
import { getVersion } from '@ms-cloudpack/package-utilities';
import { environmentInfo } from '@ms-cloudpack/environment';
import type { ReusedOptions } from '../types/ReusedOptions.js';
import type { AppPathDiscoveryMode } from '../types/AppPathDiscoveryMode.js';
import { discoverRemoteAppPaths } from './discoverRemoteAppPaths.js';
import { discoverLocalAppPaths } from './discoverLocalAppPaths.js';
import { TaskRunner } from '@ms-cloudpack/api-server';
import { readLocalConfigs, type InvalidCloudpackConfigError } from './readLocalConfigs.js';
import { readRemoteConfigs } from './readRemoteConfigs.js';
import { getCliStartTime } from './getCliStartTime.js';

export interface CommandExecutorParams<TOptions extends SharedOptions> {
  /** Command-specific arguments */
  args: string[];

  /** Command-specific and shared options. */
  options: TOptions;

  /** Function to execute the command. */
  execute: CommandAction<TOptions>;

  /** Shared program-level configuration. */
  programOptions: ProgramOptions;

  /** Verb being run, e.g. `bundle` or `cache clean`. */
  verb: string;

  /**
   * By default, the command will run against `cwd` (usually `process.cwd()`).
   * If this option is set, attempt to discover app path(s) instead.
   * (`cwd` will still be preferred if it has a `cloudpack.config.json`.)
   * See {@link AppPathDiscoveryMode} for details.
   *
   * Note that `isMultiApp` elsewhere will only be true if this is set to `'multi'` and
   * multiple apps are found.
   */
  discoverAppPaths: AppPathDiscoveryMode | undefined;

  /**
   * Whether to set `TaskReporter`'s `enableReset` option, which enables resetting console output.
   * This should only be true for commands like start which use watch mode, and needs to be set at
   * the beginning instead of being toggled by the command to ensure everything is captured.
   * (If true, the screen will also be cleared on startup.)
   */
  enableReporterReset: boolean | undefined;

  /**
   * Apply any command-specific CLI options that need to be passed through to `readConfig`.
   * (Shared and reused options are applied automatically.)
   */
  applyOptionsToConfig:
    | ((
        options: Omit<TOptions, keyof (SharedOptions & ReusedOptions)>,
      ) => Pick<ReadConfigOptions, 'extraServerConfig'> | undefined)
    | undefined;
}

interface ExtendedAppCommandContext extends AppCommandContext {
  /** Name of the app package */
  appName: string;
}

// eslint-disable-next-line no-restricted-properties -- this is the central handler
const realProcessExit = process.exit.bind(process);

/**
 * This class manages orchestration and shared state while executing a command.
 */
export class CommandExecutor<TOptions extends SharedOptions> {
  private _options: TOptions;
  private _execute: CommandAction<TOptions>;
  private _programOptions: ProgramOptions;
  private _verb: string;
  private _discoverAppPaths: AppPathDiscoveryMode | undefined;
  private _enableReporterReset: boolean;
  private _applyOptionsToConfig: CommandExecutorParams<TOptions>['applyOptionsToConfig'];
  private _reporter: TaskReporter;
  /** Items which should be disposed before a restart, as well as at process exit */
  private _autoDisposableList: AutoDisposableList;
  /** Items which should be disposed before exit only */
  private _finalDisposableList: AutoDisposableList;
  private _hasCalledExit = false;
  private _args: string[];
  private _sigintCount = 0;
  private _onInterrupt?: () => void;
  private _getMessageOnInterrupt?: () => string;

  /** Active `CommandExecutor` instances. In tests, there may be more than one. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- options type isn't used
  private static _activeInstances = new Set<CommandExecutor<any>>();
  /** Mock implementation of `process.exit` for testing purposes. */
  private static _mockProcessExit: typeof process.exit | undefined;

  /**
   * Set a mock `process.exit()` implementation for tests, to handle cases where the command would
   * call `process.exit()`.
   */
  public static mockProcessExitForTests(mockImpl: typeof process.exit | undefined): void {
    CommandExecutor._mockProcessExit = mockImpl;
  }

  /**
   * For testing use only: end all active CommandExecutor instances and call `process.exit()`
   * (which should be mocked by the test).
   */
  public static async exitAllForTests(params: CommandExitParams): Promise<void> {
    for (const instance of CommandExecutor._activeInstances) {
      await instance._exit(params);
    }
    CommandExecutor._activeInstances = new Set();
  }

  /**
   * Get a list of the commands that are running, including their arguments.
   */
  public static getRunningCommands(): string[] {
    return Array.from(CommandExecutor._activeInstances).map((instance) =>
      [instance._verb, ...instance._args].join(' '),
    );
  }

  constructor(params: CommandExecutorParams<TOptions>) {
    this._args = params.args;
    this._options = params.options;
    this._execute = params.execute;
    this._programOptions = params.programOptions;
    this._verb = params.verb;
    this._discoverAppPaths = params.discoverAppPaths;
    this._enableReporterReset = !!params.enableReporterReset;
    this._applyOptionsToConfig = params.applyOptionsToConfig;

    this._autoDisposableList = new AutoDisposableList();
    this._finalDisposableList = new AutoDisposableList();
    this._finalDisposableList.add(this._autoDisposableList);

    if (this._enableReporterReset) {
      // If running an interactive command, start by clearing the screen
      this._reporter = new TaskReporter({ enableReset: true });
      this._reporter.reset();
    } else {
      this._reporter = new TaskReporter();
    }
    // Wait to add the reporter disposable until the end

    CommandExecutor._activeInstances.add(this);
    this._finalDisposableList.add({
      dispose: () => {
        CommandExecutor._activeInstances.delete(this);

        // Command event listeners are currently only removed on final disposal since the start tests rely on
        // a 'ready' event listener carrying over across restarts.
        this._programOptions.commandEvents.removeAllListeners();
      },
    });

    // Handle SIGINT (Ctrl+C) and unhandled errors. These must be removed in _exit.
    // (Skip in tests to avoid interfering with test framework handling.)
    if (!environmentInfo.isJest) {
      process.on('SIGINT', this._sigintHandler);
      // This should also catch unhandled promise rejections
      process.on('uncaughtException', this._uncaughtExceptionHandler);
      process.on('error', this._unexpectedErrorHandler);
      // Also override process.exit in case some code calls it directly.
      // eslint-disable-next-line no-restricted-properties -- central handling code
      process.exit = ((code) => {
        // Get the call stack to display
        const error = new Error(`Unexpected process.exit(${code ?? ''}) call`);
        void this._exit({
          message: error.stack || '',
          hasErrors: true,
          exitCode: 1,
        });
      }) as typeof process.exit;

      this._finalDisposableList.add({
        dispose: () => {
          process.off('SIGINT', this._sigintHandler);
          process.off('uncaughtException', this._uncaughtExceptionHandler);
          process.off('error', this._unexpectedErrorHandler);
          // eslint-disable-next-line no-restricted-properties
          process.exit = realProcessExit;
        },
      });
    }

    // The reporter should be disposed last since it restores console methods
    this._finalDisposableList.addFinal(this._reporter);
  }

  /**
   * Run the command, including handling for restarts. This should only be called once per instance,
   * immediately after the constructor (since constructors can't be async).
   */
  public async execute(): Promise<void> {
    try {
      let shouldRestart = false;
      do {
        shouldRestart = await this._executeOnce(shouldRestart);
      } while (shouldRestart);
    } catch (error) {
      // If an error occurs, exit the process with a non-zero exit code.
      const err = error as InvalidCloudpackConfigError;
      const message = err.isExpected
        ? err.message // Only show message for expected errors
        : err.stack || err.message || String(error); // Show full stack for unexpected errors

      await this._exit({
        hasErrors: true,
        message,
      });
    }
  }

  /**
   * Execute a single iteration of the command.
   * @returns true if a restart is needed
   */
  private async _executeOnce(isRestart: boolean): Promise<boolean> {
    const contexts = await this._initializeContexts();

    if (!Array.isArray(contexts)) {
      await this._exit(contexts);
      return false;
    }

    const spanName = `${this._verb.toUpperCase().replace(/ /g, '_')}_EXECUTE`;
    const results: CommandExitParams[] = [];

    for (const context of contexts) {
      // Call the execute function which is the real command logic
      // (ie: real init command logic, real start command logic, etc.)
      const result = await context.telemetryClient.tracer.startActiveSpan(spanName, async (span) => {
        this._onInterrupt = () => {
          span.setAttribute('isInterrupted', true);
          span.end();
        };

        const isMultiApp = contexts.length > 1;
        if (isMultiApp) {
          console.log();
          console.log(`==== Starting ${this._verb} for ${context.appName} ====`);
        }

        return this._execute({
          ...context,
          span,
          isMultiApp,
          args: this._args,
          options: this._options,
          commandEvents: this._programOptions.commandEvents,
          isRestart,
          autoDispose: (disposable) => this._autoDisposableList.add(disposable),
          setInterruptMessageHandler: (getMessage) => {
            this._getMessageOnInterrupt = getMessage;
          },
        });
      });
      if (result?.isInterrupted) {
        await this._exit(result);
        return false;
      }
      if (result) {
        results.push(result);
      } else {
        // If the command returned null, this means it should keep running (e.g. start).
        // We disallow returning undefined to avoid accidents.
        throw new Error('Command executor must return exit options or null');
      }
      this._onInterrupt = undefined;
    }

    // Determine whether a command requested a restart in this iteration.
    const shouldRestart = results.some((r) => r.restart);

    if (shouldRestart) {
      // Dispose all registered disposables.
      await this._autoDisposableList.dispose();
      this._reporter.reset(true /* clearOutputs */);

      console.warn(results.map((r) => r.message).join('\n'));
    } else if (results.length) {
      // If one or more apps returned a result, exit.
      await this._exit({
        message: results.map((r) => r.message).join('\n'),
        hasErrors: results.some((r) => r.hasErrors),
        // If any app returned a non-zero exit code, use that.
        exitCode: results.find(({ exitCode }) => !!exitCode)?.exitCode,
      });
    }

    return shouldRestart;
  }

  /**
   * Determine the app paths and initialize the context for each app.
   * @returns Context(s), or an exit options object if there was an error. (might also throw on error)
   */
  private async _initializeContexts(): Promise<ExtendedAppCommandContext[] | CommandExitParams> {
    let appPaths: string[];

    if (this._discoverAppPaths) {
      // Search for cloudpack configs to determine the app path(s)
      const { app, login } = this._options as unknown as Pick<ReusedOptions, 'app' | 'login'>;
      const appPathsResult =
        this._discoverAppPaths === 'remote'
          ? await discoverRemoteAppPaths({
              options: {
                login,
                cachePath: this._options.cachePath,
                desiredApp: app,
              },
              context: { reporter: this._reporter },
            })
          : await discoverLocalAppPaths({
              cwd: this._programOptions.cwd,
              discover: this._discoverAppPaths,
              desiredApp: app,
            });
      if (!Array.isArray(appPathsResult)) {
        // This means there was an error (or the user cancelled) while getting app paths.
        return appPathsResult;
      }
      appPaths = appPathsResult;
    } else {
      // Just use the cwd
      appPaths = [this._programOptions.cwd];
    }

    // The task runner is shared between all apps so that task results can potentially be reused.
    const taskRunner = new TaskRunner();

    const readConfig = this._discoverAppPaths === 'remote' ? readRemoteConfigs : readLocalConfigs;
    const configs = await readConfig(appPaths, {
      ...this._getConfigOptionsFromCliOptions(),
      enableDefaultFeatures: true,
    });

    const helpMessages = new Set<string>(
      Object.values(configs)
        .filter((c) => c.helpMessage)
        .map((c) => c.helpMessage) as string[],
    );

    // Set reporter options and print the product info header
    this._reporter.setOptions({
      productName: 'Cloudpack',
      version: this._programOptions.version,
      description: () => `Running "${bold(this._programOptions.argv.slice(2).join(' '))}"`,
      helpMessage: [...helpMessages].join('\n'),
      plainTextMode: !this._options.color,
      logFile: !!this._options.logFile,
      cwd: this._programOptions.cwd,
      enableReset: this._enableReporterReset,
      ...(this._options.debug
        ? debugLoggingConfig
        : this._options.verbose
          ? verboseLoggingConfig
          : this._options.quiet
            ? noLoggingConfig
            : defaultLoggingConfig),
    });

    if (!this._options.debug) {
      this._reporter.ignoreLogMessage('ApplicationInsights:');
    }

    // Due to opentelemetry API changes (inability to add span processors after the tracer provider is set),
    // it's necessary to create all telemetry clients upfront. To do that, we need to get other app context first.
    const initialContexts: Record<string, Omit<ExtendedAppCommandContext, 'telemetryClient'>> = {};

    for (const appPath of Object.keys(configs)) {
      const config = configs[appPath];
      const definition = await readJson<PackageJson>(path.join(appPath, 'package.json'));
      const { name = path.basename(appPath) } = definition || {};

      initialContexts[appPath] = {
        appPath,
        appName: name,
        cachePath: this._options.cachePath,
        config,
        reporter: this._reporter,
        taskRunner,
      };
    }

    return this._initTelemetry(initialContexts);
  }

  /** Translate certain CLI options into options for `readConfig()`. */
  private _getConfigOptionsFromCliOptions(): ReadConfigOptions {
    // These options don't exist for all commands, but that's fine.
    const options = this._options as Partial<ReusedOptions & SharedOptions>;
    return {
      mode: options.mode,
      logBundleInfo: options.logBundleInfo,
      configFileName: options.config,
      // Handle --bundler by passing it as a universal package override, which will be appended
      // to any user-provided packageSettings so that it takes precedence.
      extraPackageSettings: options.bundler ? [{ match: '*', bundler: options.bundler }] : undefined,
      extraFeatures: options.features,
      extraDefineFlags: options.define,
      ...(this._applyOptionsToConfig?.(this._options) || {}),
    };
  }

  private async _initTelemetry(
    appContexts: Record<string, Omit<ExtendedAppCommandContext, 'telemetryClient'>>,
  ): Promise<ExtendedAppCommandContext[]> {
    const telemetryApi = await startTelemetry({
      productVersion: getVersion(import.meta.url),
      logLevel: this._options.verbose ? 'VERBOSE' : this._options.debug ? 'DEBUG' : undefined,
      serviceName: 'cli',
      connectionStrings: Object.fromEntries(
        Object.values(appContexts).map(({ appPath, config }) => [
          appPath,
          // Prevent accidentally picking up the telemetry connection string during tests
          environmentInfo.isJest
            ? undefined
            : config.telemetry?.connectionString || environmentInfo.cloudpack.telemetryConnectionString,
        ]),
      ),
      ...this._programOptions.telemetryOptions,
    });
    this._autoDisposableList.add({ dispose: () => telemetryApi.shutdown() });

    const contexts: ExtendedAppCommandContext[] = [];

    for (const [appPath, telemetryClient] of Object.entries(telemetryApi.clients)) {
      // Mark the start time for telemetry.
      const startTime = performance.now();

      const { config, appName } = appContexts[appPath];
      contexts.push({
        ...appContexts[appPath],
        telemetryClient,
      });

      telemetryClient.tracer.startSpan('CLI_ENTRY', { startTime: getCliStartTime() }).end();

      await telemetryClient.tracer.startActiveSpan('INIT_TELEMETRY', { startTime }, (span) => {
        const enabledFeatureNames = Object.entries(config.features || {})
          .filter(([, value]) => value)
          .map(([key]) => key);

        if (enabledFeatureNames.length) {
          console.log(`Enabled features for ${appName}: ${enabledFeatureNames.map((f) => yellow(f)).join(', ')}`);
        }

        const telemetryAttributes = {
          verb: this._verb,
          arguments: this._programOptions.argv.slice(3).join(' '),
          environment: environmentInfo.isCI ? 'ci' : environmentInfo.isCodespaces ? 'codespaces' : 'local',
          appName: appName || '<unknown>',
          mode: config.mode,
          features: enabledFeatureNames,
          externalCorrelationId: environmentInfo.cloudpack.telemetryExternalCorrelationId,
          // Azure DevOps build attributes
          ...environmentInfo.ado,
        };

        for (const [key, value] of Object.entries(telemetryAttributes)) {
          if (value) {
            span.setAttribute(key, value);
          }
        }
      });
    }

    return contexts;
  }

  private _sigintHandler = (): void => {
    this._sigintCount++;

    if (this._sigintCount > 1) {
      console.debug('Forcing exit');

      // We are forcing the process to exit.
      // No clean-up is needed because user pressed Ctrl+C twice.
      this._callProcessExit(1);
      return;
    }

    this._onInterrupt?.();

    // We are trying to exit gracefully.
    void this._exit({ isInterrupted: true, message: '' });
  };

  private _uncaughtExceptionHandler: NodeJS.UncaughtExceptionListener = (error, reason) => {
    const errorType = reason === 'uncaughtException' ? 'Uncaught exception' : 'Unhandled promise rejection';
    const message = `${errorType}: ${error.stack || error.message || String(error)}`;
    // Go ahead and log the message in case the process exits before cleanup is complete
    console.error(message);

    void this._exit({ message, hasErrors: true, exitCode: 1 });
  };

  private _unexpectedErrorHandler = (...args: unknown[]): void => {
    const err = new Error('Unexpected process error');
    console.error(err.stack);
    console.error('Error details:', ...args);
  };

  /**
   * Exit function called with the result returned by the command's execute function,
   * or manually via `CommandExecutor.exitAll()` in tests.
   *
   * Except in tests, execution can be assumed to have stopped after this is called.
   * (In tests, `process.exit` is probably mocked to throw.)
   */
  private async _exit(params: CommandExitParams): Promise<void> {
    const { hasErrors = this._reporter.hasErrors(), exitCode = hasErrors ? 1 : 0 } = params;
    const message = params.isInterrupted ? this._getMessageOnInterrupt?.() || 'Interrupted by user' : params.message;

    if (this._hasCalledExit) {
      // Skip most of the operations if exit() was called multiple times.
      // But only use the error message if not in a test, due to the way we're mocking process.exit.
      if (!environmentInfo.isJest) {
        const prettyParams = Object.entries(params)
          .map(([key, value]) => `${key}="${value}"`)
          .join(', ');
        console.warn(`exit() was called multiple times. This call's options: ${prettyParams}.`);
      }
    } else {
      this._hasCalledExit = true;

      // Show the summary message (this will throw if called twice).
      this._reporter.complete({
        summary: hasErrors ? red(message) : message,
        showHelp: exitCode !== 0,
      });

      // Dispose all resources
      await this._finalDisposableList.dispose();
    }

    this._callProcessExit(exitCode);
  }

  /** Call the mocked or real `process.exit()` function */
  private _callProcessExit(exitCode: number): void {
    (CommandExecutor._mockProcessExit || realProcessExit)(exitCode);
  }
}
